import { NODE_ENV_ZOD } from "@connected-repo/zod-schemas/node_env";
import { object, preprocess, string, url } from "zod";

const optionalString = preprocess(
	(val) => (val === "" ? undefined : val),
	string().optional(),
);
const optionalUrl = preprocess((val) => (val === "" ? undefined : val), url().optional());

// Empty string is a valid production value — signals "same-origin/relative"
// when the frontend is served behind a reverse proxy (Dokploy/nginx setup)
// that forwards /api/* to the backend on the same domain. Consumers must
// tolerate an empty base and build relative URLs.
const apiUrlOrEmpty = preprocess(
	(val) => (val === "" ? undefined : val),
	url("API URL must be a valid URL").optional(),
);

/**
 * `#rrggbb`. Also accepts `rrggbb` and stray quotes: in a `.env`-style box
 * (e.g. Dokploy's Environment tab) an unquoted `#` starts a comment, so
 * `E00000` or `"#E00000"` is how the colour survives there.
 */
export const normalizeHexColor = (val: unknown): unknown => {
	if (typeof val !== "string") return val;
	const trimmed = val.trim().replace(/^["']|["']$/g, "").trim();
	if (trimmed === "") return undefined;
	return /^[0-9a-fA-F]{6}$/.test(trimmed) ? `#${trimmed}` : trimmed;
};

const optionalHexColor = preprocess(
	normalizeHexColor,
	string()
		.regex(/^#[0-9a-fA-F]{6}$/, "hex colour like #1976d2 (or 1976d2)")
		.optional(),
);

export const envSchemaZod = object({
	VITE_USER_NODE_ENV: NODE_ENV_ZOD,
	// ── Branding fallbacks for first paint; the live values come from
	// GET /api/brand/branding.json (admin Settings). See BrandingContext. ──
	VITE_APP_NAME: string().min(1).max(60),
	VITE_APP_SHORT_NAME: optionalString,
	VITE_APP_TAGLINE: optionalString,
	VITE_GARMENT_NOUN: optionalString,
	VITE_BRAND_PRIMARY_COLOR: optionalHexColor,
	VITE_BRAND_SECONDARY_COLOR: optionalHexColor,
	VITE_BRAND_LOGO_URL: optionalString,
	VITE_API_URL: apiUrlOrEmpty,
	// Empty in the same-origin Dokploy deploy — consumers fall back to
	// window.location.origin so OAuth callbacks stay on the visible domain.
	VITE_USER_APP_URL: apiUrlOrEmpty,
	VITE_TEST_USERNAME: optionalString,
	// Tamil-language salesman seeded for e2e (backend test fixtures).
	VITE_TEST_MEMBER_USERNAME: optionalString,
	VITE_TEST_PASSWORD: string().min(8, "Test password must be at least 8 characters").optional(),
	// "true" shows "Continue with Google" (sign-in for existing logins only).
	VITE_GOOGLE_SIGNIN_ENABLED: optionalString,
	VITE_OTEL_SERVICE_NAME: string().min(1),
	VITE_SENTRY_DSN: preprocess((val) => (val === "" ? undefined : val), url().optional()),
	VITE_SENTRY_ENV: string().optional(),
	VITE_SENTRY_RELEASE: string().optional(),
	VITE_SENTRY_ORG: string().optional(),
	VITE_SENTRY_PROJECT: string().optional(),
	// Novu — Inbox connects the browser directly to Novu; empty = notification UI hidden.
	VITE_NOVU_APP_IDENTIFIER: optionalString,
	VITE_NOVU_API_URL: optionalUrl,
	VITE_NOVU_SOCKET_URL: optionalString,
	// Firebase Web (for FCM push) — empty = push disabled, other channels still work.
	VITE_FIREBASE_API_KEY: optionalString,
	VITE_FIREBASE_AUTH_DOMAIN: optionalString,
	VITE_FIREBASE_PROJECT_ID: optionalString,
	VITE_FIREBASE_MESSAGING_SENDER_ID: optionalString,
	VITE_FIREBASE_APP_ID: optionalString,
	VITE_FIREBASE_VAPID_KEY: optionalString,
});
